import React, { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Descriptions,
  List,
  Result,
  Row,
  Space,
  Spin,
  Tabs,
  Typography,
  notification,
} from 'antd';
import ImageGallery from 'react-image-gallery';

import {
  useGetActiveAuctionQuery,
  useGetLiveAuctionStatusQuery,
} from '../../../app/api/auctionApiSlice';
import LiveAuctionStatus from '../../../core/models/LiveAuctionStatus';
import AuctionItem from '../../../core/models/AuctionItem';
import { useNavigate } from 'react-router-dom';
import { Bid } from '../../../core/models/Bid';
import moment from 'moment';
import { convertToCurrencyString } from '../../../utils/currencyUtil';
import ViewVehicleDetails from '../../vehicles/containers/ViewVehicleDetails';
import { isJson } from '../../../utils/jsonUtil';
import { skipToken } from '@reduxjs/toolkit/dist/query';

const { Title, Text } = Typography;

const ws = new WebSocket(`${process.env.REACT_APP_AUTOSTREAM_WS!}/auction/id`);

const LiveAuction: React.FC = () => {
  const navigate = useNavigate();

  const { data: auction, isLoading: isAuctionLoading } = useGetActiveAuctionQuery();
  const { data: initialAuctionStatus, isLoading: isAuctionStatusLoading } =
    useGetLiveAuctionStatusQuery(auction ? auction.id : skipToken);

  const [liveAuctionStatus, setLiveAuctionStatus] = useState<LiveAuctionStatus>();
  const [activeAuctionItem, setActiveAuctionItem] = useState<AuctionItem>();

  useEffect(() => {
    ws.onopen = (event) => {
      console.log('Connected to WebSocket');
    };

    ws.onmessage = (event) => {
      if (!isJson(event.data)) return;

      try {
        const liveAuctionStatus: LiveAuctionStatus = JSON.parse(event.data);

        if (liveAuctionStatus.event === 'MoveToNext' || liveAuctionStatus.event === 'Stop') {
          if (liveAuctionStatus.auctionStatus?.previousAuctionItemStatus?.status === 'Sold') {
            notification.success({
              message: 'Previous Vehicle Auction Status',
              description: (
                <>
                  Previous vehicle was{' '}
                  <Text type='success'>
                    {liveAuctionStatus.auctionStatus?.previousAuctionItemStatus?.status.toUpperCase()}
                  </Text>
                </>
              ),
            });
          } else if (
            liveAuctionStatus.auctionStatus?.previousAuctionItemStatus?.status === 'Unsold'
          ) {
            notification.warning({
              message: 'Previous Vehicle Auction Status',
              description: (
                <>
                  Previous vehicle was{' '}
                  <Text type='warning'>
                    {liveAuctionStatus.auctionStatus?.previousAuctionItemStatus?.status.toUpperCase()}
                  </Text>
                </>
              ),
            });
          }
        }

        setAuctionStatus(liveAuctionStatus);
      } catch (error: any) {
        notification.error({ message: 'Live Auction Status', description: error });
      }
    };
  });

  useEffect(() => {
    if (initialAuctionStatus) {
      setAuctionStatus({ auctionStatus: initialAuctionStatus, event: 'Start' });
    }
  }, [initialAuctionStatus]);

  const setAuctionStatus = (status: LiveAuctionStatus): void => {
    setLiveAuctionStatus(status);
    const activeItem = getActiveAuctionItem(status);
    setActiveAuctionItem(activeItem);
    if (status.event === 'Stop') {
      navigate(`/auctions/result`);
    }
  };

  const getActiveAuctionItem = (auctionStatus: LiveAuctionStatus): AuctionItem | undefined => {
    if (auction && auctionStatus)
      return auction.auctionItems.find(
        (item: AuctionItem) => item.id === auctionStatus?.auctionStatus?.activeAuctionItemId,
      );
  };

  if (isAuctionLoading || isAuctionStatusLoading)
    return (
      <Row justify={'center'}>
        <Space direction='vertical' align='center'>
          <Spin />
          <span>Loading...</span>
        </Space>
      </Row>
    );

  if (!auction) return <Result status='info' title='No Active Auction'></Result>;

  return (
    <div>
      {liveAuctionStatus?.auctionStatus?.status.id === 1 && (
        <Result
          status='info'
          title='Auction has not started yet'
          extra={
            <>
              <Text style={{ fontWeight: '600' }}>Auction Date/Time:</Text>
              <Text style={{ fontWeight: '400' }}>
                {moment(auction?.auctionDate).format('DD/MM/YYYY HH:mm')}
              </Text>
              <br />
              <Text>
                {moment.duration(moment(auction?.auctionDate).diff(moment()).toString()).humanize()}{' '}
                to go
              </Text>
            </>
          }
        />
      )}
      {liveAuctionStatus?.auctionStatus?.status.id === 2 && activeAuctionItem !== undefined && (
        <>
          <Row>
            <Col span={12}>
              <Text style={{ fontWeight: '600', fontSize: 18 }}>{`${
                activeAuctionItem?.vehicle.name ?? ''
              }`}</Text>
            </Col>
          </Row>
          <br />
          <Row gutter={12}>
            <Col span={14} offset={2}>
              <Tabs>
                <Tabs.TabPane tab='Vehicle Details' key='vehicle'>
                  <ImageGallery
                    items={activeAuctionItem.vehicle.images?.map((image) => ({
                      original: image.filePath,
                      thumbnail: image.filePath,
                    }))}
                    showFullscreenButton={false}
                    showPlayButton={false}
                    lazyLoad={true}
                  />
                  <ViewVehicleDetails vehicle={activeAuctionItem.vehicle} layout='vertical' />
                </Tabs.TabPane>
                {activeAuctionItem?.vehicle?.inspectionReportUrl && (
                  <Tabs.TabPane tab='Grading Report' key='inspection'>
                    <iframe
                      width={600}
                      height={900}
                      src={`${activeAuctionItem?.vehicle?.inspectionReportUrl}?view=fitH`}
                    ></iframe>
                  </Tabs.TabPane>
                )}
              </Tabs>
            </Col>
            <Col span={6}>
              <Card title={<Title level={4}>Live Auction</Title>}>
                <Descriptions
                  bordered={false}
                  layout='vertical'
                  labelStyle={{ fontWeight: 'bold' }}
                  column={2}
                  size='small'
                  colon={false}
                >
                  <Descriptions.Item label='Starting Price'>
                    {convertToCurrencyString(activeAuctionItem.startingPrice)}
                  </Descriptions.Item>
                  <Descriptions.Item label='Reserve Price'>
                    {activeAuctionItem.reservePrice
                      ? convertToCurrencyString(activeAuctionItem.reservePrice)
                      : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
                <br />
                <List
                  itemLayout='horizontal'
                  size='small'
                  style={{ maxHeight: '100vh', overflow: 'auto' }}
                >
                  {(liveAuctionStatus?.auctionStatus?.activeAuctionItemBids === null ||
                    (liveAuctionStatus?.auctionStatus?.activeAuctionItemBids &&
                      liveAuctionStatus?.auctionStatus?.activeAuctionItemBids?.length <= 0)) && (
                    <Alert message='No bids' type='warning' />
                  )}
                  {liveAuctionStatus?.auctionStatus?.activeAuctionItemBids?.map((bid: Bid) => (
                    <List.Item key={bid.bidPrice}>
                      <Text>{convertToCurrencyString(bid.bidPrice)}</Text>
                    </List.Item>
                  ))}
                </List>
              </Card>
            </Col>
          </Row>
        </>
      )}
      {liveAuctionStatus?.auctionStatus?.status.id === 2 && activeAuctionItem === undefined && (
        <Result status='info' title='No Active Auction Item' />
      )}
      {liveAuctionStatus?.auctionStatus?.status.id === 3 && (
        <Result
          status='info'
          title='Auction Paused'
          extra={
            <>
              <Text>Auction has been paused, please wait for it to resume</Text>
            </>
          }
        />
      )}
    </div>
  );
};

export default LiveAuction;
